import { Link } from "react-router-dom";
import { Home, Briefcase, Clock, CheckCircle2, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Shows where the account stands in lister onboarding, on both profile pages.
//   - Private lister: active, can post.
//   - Realtor pending: under review, cannot post yet.
//   - Realtor approved: verified with perks.
//   - Realtor rejected: honest reason + resubmit.
// Renders nothing for accounts that never started lister onboarding.
export default function ListerStatusCard() {
  const { user } = useAuth();
  if (!user) return null;

  const kind = user.lister_kind;
  const status = user.realtor_status;
  if (!kind) return null;

  let cfg;
  if (kind === "private") {
    cfg = {
      icon: Home,
      tone: "lime",
      title: "Owner",
      chip: "Active",
      body: "Your account can post listings. Your first listing is free.",
    };
  } else if (kind === "realtor" && status === "approved") {
    cfg = {
      icon: CheckCircle2,
      tone: "lime",
      title: "Verified realtor",
      chip: "Approved",
      body: "You are an approved realtor with the verified badge and extra visibility. Switch to lister mode to post.",
    };
  } else if (kind === "realtor" && status === "rejected") {
    cfg = {
      icon: AlertCircle,
      tone: "amber",
      title: "Realtor application",
      chip: "Not approved",
      body:
        user.realtor_reject_note ||
        "Your application was not approved. You can resubmit with corrected details.",
      action: { to: "/become-a-lister/realtor", label: "Resubmit application" },
    };
  } else {
    // realtor pending (default)
    cfg = {
      icon: Clock,
      tone: "amber",
      title: "Realtor application",
      chip: "Under review",
      body: "Our team is reviewing your application. You will be notified once approved. You cannot post listings until then.",
    };
  }

  const Icon = cfg.icon;
  const chipClass =
    cfg.tone === "lime"
      ? "bg-brand-tint text-brand-lime"
      : "bg-amber-50 text-amber-700";
  const iconWrap =
    cfg.tone === "lime"
      ? "bg-brand-tint text-brand-lime"
      : "bg-amber-50 text-amber-600";

  return (
    <section className="mt-4 flex flex-wrap items-start justify-between gap-3 rounded-3xl border border-brand-line bg-white p-6 shadow-hairline">
      <div className="flex items-start gap-3">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${iconWrap}`}>
          {kind === "realtor" && status !== "approved" && status !== "rejected" ? (
            <Briefcase size={17} />
          ) : (
            <Icon size={17} />
          )}
        </span>
        <div>
          <p className="text-[15px] font-semibold text-brand-ink">
            {cfg.title}
            <span className={`ml-2 rounded-full px-2.5 py-0.5 text-xs font-semibold ${chipClass}`}>
              {cfg.chip}
            </span>
          </p>
          <p className="mt-0.5 max-w-md text-xs text-brand-secondary">{cfg.body}</p>
          {cfg.action ? (
            <Link
              to={cfg.action.to}
              className="mt-2 inline-flex items-center rounded-full border border-brand-line bg-white px-3.5 py-1.5 text-xs font-semibold text-brand-ink transition hover:border-brand-ink"
            >
              {cfg.action.label}
            </Link>
          ) : null}
        </div>
      </div>
    </section>
  );
}
